import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/sidebar.css";

const Sidebar = () => {
    const [activeCategory, setActiveCategory] = useState("Artificial Intelligence");
    const contentRefs = useRef({});
    const sidebarRef = useRef(null);
    const navigate = useNavigate();

    const categories = [
        { id: 1, name: "AI/ML Services", icon: "🤖", route: "/services/ai-services" },
        { id: 2, name: "App Development", icon: "📱", route: "/services/app-development" },
        { id: 3, name: "Website Development", icon: "💻", route: "/services/website-development" },
        { id: 4, name: "Digital Marketing", icon: "📈", route: "/services/digital-marketing" },
        { id: 5, name: "Data Center", icon: "🗄️", route: "/services/data-center" },
        // { id: 6, name: "Internet of Things (IoT)", icon: "🌐" },
        { id: 7, name: "IT Consulting", icon: "🧰", route: "/services/it-consulting" },
        { id: 8, name: "Cyber Security", icon: "🔐", route: "/services/cyber-security" },
        { id: 9, name: "Cloud Services", icon: "☁️", route: "/services/cloud-services" },
    ];

    const contentData = {
        "AI/ML Services": {
            tag: "#AI/ML Services",
            title: "Build intelligent systems that learn, predict, and automate with precision.",
            image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&q=80",
            features: [
                { title: "Custom AI/ML Model Development", icon: "✓" },
                { title: "Natural Language Processing (NLP)", icon: "✓" },
                { title: "Computer Vision Applications", icon: "✓" },
                { title: "Model Training & Optimization", icon: "✓" },
            ],
        },
        "App Development": {
            tag: "#App Development",
            title: "Build robust and scalable applications for various platforms.",
            image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&q=80",
            features: [
                { title: "Cross-Platform Development", icon: "✓" },
                { title: "API Integration", icon: "✓" },
                { title: "User Experience Design", icon: "✓" },
                { title: "Performance Optimization", icon: "✓" },
            ],
        },
        "Website Development": {
            tag: "#Website Development",
            title: "Create responsive and high-performance websites.",
            image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&q=80",
            features: [
                { title: "Responsive Design", icon: "✓" },
                { title: "Performance Optimization", icon: "✓" },
                { title: "SEO Best Practices", icon: "✓" },
                { title: "Content Management Systems", icon: "✓" },
            ],
        },
        "Digital Marketing": {
            tag: "#Digital Marketing",
            title: "Strategies to enhance online presence and engagement.",
            image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80",
            features: [
                { title: "Social Media Marketing", icon: "✓" },
                { title: "Content Marketing", icon: "✓" },
                { title: "SEO Optimization", icon: "✓" },
                { title: "Email Marketing", icon: "✓" },
            ],
        },
        "Data Center": {
            tag: "#Data Center",
            title: "Transform raw data into actionable insights and strategic decisions.",
            image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80",
            features: [
                { title: "High Availability & Uptime", icon: "✓" },
                { title: "Advanced Data Security", icon: "✓" },
                { title: "Scalable Infrastructure", icon: "✓" },
                { title: "24/7 Monitoring & Support", icon: "✓" },
            ],
        },
        // "Internet of Things (IoT)": {
        //     tag: "#IoT",
        //     title: "Connect devices and systems for smarter automation and control.",
        //     image: "https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?w=1200&q=80",
        //     features: [
        //         { title: "IoT Device Integration", icon: "✓" },
        //         { title: "Smart Home Solutions", icon: "✓" },
        //         { title: "Industrial IoT", icon: "✓" },
        //         { title: "IoT Security", icon: "✓" },
        //     ],
        // },
        "IT Consulting": {
            tag: "#IT Consulting",
            title: "Expert guidance to navigate the digital landscape.",
            image: "https://images.unsplash.com/photo-1592478411213-6153e4ebc07d?w=1200&q=80",
            features: [
                { title: "Technology Strategy & Planning", icon: "✓" },
                { title: "Digital Transformation Consulting", icon: "✓" },
                { title: "System & Process Optimization", icon: "✓" },
                { title: "Risk Assessment & IT Governance", icon: "✓" },
            ],
        },
        "Cyber Security": {
            tag: "#Cyber Security",
            title: "Protecting systems and data from cyber threats.",
            image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80",
            features: [
                { title: "Threat Detection & Response", icon: "✓" },
                { title: "Security Audits & Compliance", icon: "✓" },
                { title: "Incident Management", icon: "✓" },
                { title: "Performance Analytics", icon: "✓" },
            ],
        },
        "Cloud Services": {
            tag: "#Cloud Services",
            title: "Flexible and scalable cloud solutions for modern businesses.",
            image: "https://images.unsplash.com/photo-1555255707-c07966088b7b?w=1200&q=80",
            features: [
                { title: "Cloud Infrastructure Setup", icon: "✓" },
                { title: "Scalable & Flexible Resources", icon: "✓" },
                { title: "Cloud Security & Compliance", icon: "✓" },
                { title: "Backup & Disaster Recovery", icon: "✓" },
            ],
        },
    };

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + window.innerHeight / 2;

            // Check each content section
            for (const category of categories) {
                const element = contentRefs.current[category.name];
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                        setActiveCategory(category.name);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [categories]);

    const scrollToSection = (categoryName) => {
        const element = contentRefs.current[categoryName];
        if (element) {
            const offsetTop = element.offsetTop - 100; // 100px offset for better UX
            window.scrollTo({
                top: offsetTop,
                behavior: 'smooth'
            });
        }
    };

    const handleTransformClick = (categoryName) => {
        const category = categories.find(cat => cat.name === categoryName);
        if (category && category.route) {
            navigate(category.route);
        }
    };

    return (
        <section className="ai-solutions-section">
            <div className="container">
                {/* Header */}
                <div className="solutions-header text-center mb-5">
                    <h2 className="solutions-title">AI Solutions Designed to Drive Growth</h2>
                    <p className="solutions-subtitle">
                        Discover our full-suite AI capabilities that accelerate your digital transformation journey.
                    </p>
                </div>

                <div className="row">
                    {/* Left Sidebar - Sticky */}
                    <div className="col-lg-3 col-md-12 col-sm-12">
                        <div className="sidebar-nav sidebar-sticky" ref={sidebarRef}>
                            {categories.map((category) => (
                                <button
                                    key={category.id}
                                    className={`sidebar-item ${activeCategory === category.name ? "active" : ""
                                        }`}
                                    onClick={() => scrollToSection(category.name)}
                                >
                                    <span className="sidebar-icon">{category.icon}</span>
                                    <span className="sidebar-text">{category.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Right Content Area - Scrollable */}
                    <div className="col-lg-9 col-md-12 col-sm-12">
                        {categories.map((category) => {
                            const content = contentData[category.name];
                            return (
                                <div
                                    key={category.id}
                                    ref={(el) => (contentRefs.current[category.name] = el)}
                                    className="content-section"
                                >
                                    <div className="content-area">
                                        {/* Main Image with Overlay */}
                                        <div className="content-image-wrapper">
                                            <img
                                                src={content.image}
                                                alt={category.name}
                                                className="content-image"
                                            />
                                            <div className="content-overlay">
                                                <span className="content-tag">{content.tag}</span>
                                                <h3 className="content-title">{content.title}</h3>
                                                <button
                                                    className="btn-transform"
                                                    onClick={() => handleTransformClick(category.name)}
                                                >
                                                    <span>Transform with us</span>
                                                    <svg
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M7 17L17 7M17 7H7M17 7V17"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Features Grid */}
                                        <div className="features-grid">
                                            {content.features.map((feature, index) => (
                                                <div key={index} className="feature-box">
                                                    <div className="feature-icon-circle">
                                                        <svg
                                                            width="24"
                                                            height="24"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M20 6L9 17L4 12"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <h4 className="feature-title">{feature.title}</h4>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Sidebar;
